import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Mic, Radio, Clock, User, Volume2, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { SHOW_ZELLO_SECTIONS } from '../lib/featureFlags';

const POLL_INTERVAL_MS = 12000;
const ZELLO_PROVIDER = (process.env.REACT_APP_ZELLO_PROVIDER || 'zello-channel').trim() || 'zello-channel';
const ZELLO_BASE_PATH = `/api/zello/${ZELLO_PROVIDER}`;
const HIDE_VOICE_TRANSCRIPTIONS = ZELLO_PROVIDER === 'zello-channel';

const formatTimestamp = (value) => {
  if (!value) {
    return 'Unknown time';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown time';
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (durationMs) => {
  if (!durationMs || durationMs <= 0) {
    return null;
  }

  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const PublicChatSection = () => {
  const [messages, setMessages] = useState([]);
  const [channelName, setChannelName] = useState('Public Chat');
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!SHOW_ZELLO_SECTIONS) {
      return undefined;
    }

    let isMounted = true;

    const loadFeed = async () => {
      try {
        const response = await apiFetch(`${ZELLO_BASE_PATH}/public-chat?limit=20`);
        if (!response.ok) {
          if (isMounted) {
            setLoadError(true);
            setMessages([]);
          }
          return;
        }

        const payload = await response.json();
        if (!isMounted) {
          return;
        }

        setChannelName(typeof payload.channel === 'string' && payload.channel.trim() ? payload.channel : 'Public Chat');
        setFetchedAt(typeof payload.fetchedAt === 'string' ? payload.fetchedAt : null);
        setMessages(Array.isArray(payload.messages) ? payload.messages : []);
        setLoadError(false);
      } catch {
        if (isMounted) {
          setLoadError(true);
          setMessages([]);
        }
      }
    };

    loadFeed();
    const pollTimer = setInterval(loadFeed, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(pollTimer);
    };
  }, []);

  const textMessages = useMemo(
    () => messages.filter((message) => message.type === 'text' && typeof message.text === 'string' && message.text.trim().length > 0),
    [messages]
  );
  const voiceMessages = useMemo(
    () => messages.filter((message) => message.type === 'voice'),
    [messages]
  );
  const imageMessages = useMemo(
    () => messages.filter((message) => message.type === 'image' && typeof message.imageUrl === 'string' && message.imageUrl.trim().length > 0),
    [messages]
  );

  if (!SHOW_ZELLO_SECTIONS) {
    return null;
  }

  return (
    <section id="public-chat" className="py-24 bg-gray-950 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/4 w-[360px] h-[360px] bg-red-500/10 rounded-full filter blur-[140px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[420px] h-[420px] bg-red-500/5 rounded-full filter blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
            <Radio size={14} className="animate-pulse" />
            Live Channel Feed
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Public Chat
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Read-only live updates from our Zello "{channelName}" channel.
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Last synced: {formatTimestamp(fetchedAt)}
          </p>
        </motion.div>

        {loadError ? (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ amount: 0.2 }}
            className="text-center p-10 rounded-2xl bg-gray-900/60 border border-gray-800"
          >
            <Radio size={40} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-300">
              Unable to retrieve public chat feed, please try again later.
            </p>
          </motion.div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.2 }}
                className="rounded-2xl border border-gray-800 bg-gray-900/70 backdrop-blur-sm"
              >
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-800">
                  <MessageSquare size={18} className="text-red-400" />
                  <h3 className="text-white font-semibold">Public Text Chat</h3>
                </div>
                <div className="max-h-[520px] overflow-y-auto p-4 space-y-3">
                  {textMessages.length > 0 ? (
                    textMessages.map((message) => (
                      <div key={`text-${message.id}`} className="rounded-xl border border-gray-800 bg-black/40 p-4">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="inline-flex items-center gap-2 text-gray-200 text-sm font-medium">
                            <User size={14} className="text-red-400" />
                            {message.sender || 'Unknown'}
                          </span>
                          <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                            <Clock size={12} />
                            {formatTimestamp(message.timestampIso)}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm whitespace-pre-line">
                          {message.text}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm p-2">
                      No text messages are currently available.
                    </p>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.2 }}
                transition={{ delay: 0.05 }}
                className="rounded-2xl border border-gray-800 bg-gray-900/70 backdrop-blur-sm"
              >
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-800">
                  <Mic size={18} className="text-red-400" />
                  <h3 className="text-white font-semibold">Live Voice Feed</h3>
                </div>
                <div className="max-h-[520px] overflow-y-auto p-4 space-y-3">
                  {voiceMessages.length > 0 ? (
                    voiceMessages.map((message) => (
                      <div key={`voice-${message.id}`} className="rounded-xl border border-gray-800 bg-black/40 p-4">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <span className="inline-flex items-center gap-2 text-gray-200 text-sm font-medium">
                            <Volume2 size={14} className="text-red-400" />
                            {message.sender || 'Unknown'}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {formatTimestamp(message.timestampIso)}
                          </span>
                        </div>

                        {message.audioUrl ? (
                          <audio
                            controls
                            preload="none"
                            controlsList="nodownload noplaybackrate"
                            disablePictureInPicture
                            src={message.audioUrl}
                            className="w-full mb-2"
                          />
                        ) : (
                          <p className="text-gray-500 text-sm mb-2">
                            Voice media is still processing.
                          </p>
                        )}

                        <div className={`flex items-center text-xs text-gray-500 ${HIDE_VOICE_TRANSCRIPTIONS ? 'justify-end' : 'justify-between'}`}>
                          {!HIDE_VOICE_TRANSCRIPTIONS ? (
                            <span>{message.text || 'No transcription available.'}</span>
                          ) : null}
                          <span>{formatDuration(message.duration) || ''}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm p-2">
                      No voice messages are currently available.
                    </p>
                  )}
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
              transition={{ delay: 0.08 }}
              className="mt-6 rounded-2xl border border-gray-800 bg-gray-900/70 backdrop-blur-sm"
            >
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-800">
                <ImageIcon size={18} className="text-red-400" />
                <h3 className="text-white font-semibold">Shared Images</h3>
              </div>
              <div className="p-4">
                {imageMessages.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {imageMessages.map((message) => (
                      <div key={`img-${message.id}`} className="rounded-xl border border-gray-800 bg-black/40 overflow-hidden">
                        <a
                          href={message.imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={message.imageUrl}
                            alt={`Shared by ${message.sender || 'Unknown'}`}
                            className="w-full h-44 object-cover hover:opacity-90 transition"
                            loading="lazy"
                          />
                        </a>
                        <div className="p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-200 text-sm font-medium">{message.sender || 'Unknown'}</span>
                            <a
                              href={message.imageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-400 hover:text-red-300"
                              title="Open image"
                            >
                              <ExternalLink size={13} />
                            </a>
                          </div>
                          <p className="text-gray-500 text-xs mt-1">{formatTimestamp(message.timestampIso)}</p>
                          {message.text ? (
                            <p className="text-gray-400 text-xs mt-2 whitespace-pre-line">{message.text}</p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm p-2">
                    No image messages are currently available.
                  </p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
};

export default PublicChatSection;
